import { useState, useCallback } from 'react';

import toast from 'components/partials/toast/toast';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';

import {
  executeModalAction,
  buildGroupActions as buildGroupActionsHelper,
  buildRegimeActions as buildRegimeActionsHelper,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-helpers';
import type {
  ModalState,
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface PendingMutation {
  isPending: boolean;
}

interface MutationLike extends PendingMutation {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface UseRegimesSidebarModalActionsParams {
  userId: string;
  modelId: string | number;
  groups: RegimeGroup[];
  regimes: RegimeItem[];
  mutations: Record<string, MutationLike>;
}

interface UseRegimesSidebarModalActionsResult {
  modalState: ModalState | null;
  isModalLoading: boolean;
  handleModalConfirm: (arg?: string | number | null) => Promise<void>;
  buildGroupActions: (group: RegimeGroup) => ActionsDropdownOption[];
  buildRegimeActions: (regime: RegimeItem) => ActionsDropdownOption[];
  closeModal: () => void;
}

export const useRegimesSidebarModalActions = ({
  userId,
  modelId,
  groups,
  regimes,
  mutations,
}: UseRegimesSidebarModalActionsParams): UseRegimesSidebarModalActionsResult => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const handleModalConfirm = useCallback(
    async (arg?: string | number | null) => {
      if (!modalState) return;

      try {
        await executeModalAction({
          type: modalState.type,
          entity: modalState.entity,
          arg,
          mutations,
          userId,
          modelId,
          regimes,
        });

        setModalState(null);
      } catch {
        toast.error({
          title: 'Error',
          message: 'Something went wrong. Please try again.',
        });
      }
    },
    [modalState, mutations, regimes, userId, modelId],
  );

  const isModalLoading = Object.values(mutations).some((mutation) => mutation.isPending);

  const buildGroupActions = useCallback(
    (group: RegimeGroup) =>
      buildGroupActionsHelper(
        group as unknown as Record<string, unknown>,
        setModalState as (state: unknown) => void
      ) as ActionsDropdownOption[],
    [],
  );

  const buildRegimeActions = useCallback(
    (regime: RegimeItem) =>
      buildRegimeActionsHelper(
        regime as unknown as Record<string, unknown>,
        groups.length,
        setModalState as (state: unknown) => void
      ) as ActionsDropdownOption[],
    [groups.length],
  );

  return {
    modalState,
    isModalLoading,
    handleModalConfirm,
    buildGroupActions,
    buildRegimeActions,
    closeModal: useCallback(() => setModalState(null), []),
  };
};
